// Redirect host MUST come from window.location.origin – do not hardcode domains.
import { supabase } from '@/integrations/supabase/client';

/**
 * SSR guard: Ensures we're in a browser environment when computing redirect URLs
 */
function getRedirectOrigin(): string {
  // Prefer VITE_APP_URL (set in Vercel for staging/prod), fallback to window.location.origin
  if (typeof import.meta !== 'undefined' && (import.meta as any).env?.VITE_APP_URL) {
    return (import.meta as any).env.VITE_APP_URL;
  }
  if (typeof window === 'undefined' || !window.location?.origin) {
    throw new Error('emailRedirectTo requires a browser origin');
  }
  return window.location.origin;
}

/**
 * Request a magic link for existing users to sign in
 */
export async function requestMagicLink(email: string): Promise<void> {
  const redirectUrl = `${getRedirectOrigin()}/auth/callback?next=/dashboard`;
  
  const { error } = await supabase.auth.signInWithOtp({
    email,
    options: {
      emailRedirectTo: redirectUrl
    }
  });
  
  if (error) {
    throw error;
  }
}

/**
 * Request a magic link for new users to sign up (passwordless)
 * Note: Uses signInWithOtp for passwordless flow. User will be created on first sign-in.
 */
export async function requestSignupMagicLink(email: string): Promise<void> {
  const redirectUrl = `${getRedirectOrigin()}/auth/callback?next=/dashboard`;
  
  const { error } = await supabase.auth.signInWithOtp({
    email,
    options: {
      emailRedirectTo: redirectUrl,
      shouldCreateUser: true
    }
  });
  
  if (error) {
    throw error;
  }
}

/**
 * Resend signup confirmation email
 */
export async function resendSignupConfirmation(email: string): Promise<void> {
  const redirectUrl = `${getRedirectOrigin()}/auth/callback?next=/dashboard`;
  
  const { error } = await supabase.auth.resend({
    type: 'signup',
    email,
    options: {
      emailRedirectTo: redirectUrl
    }
  });
  
  if (error) {
    throw error;
  }
}

/**
 * Request a password reset email
 */
export async function requestPasswordReset(email: string): Promise<void> {
  const redirectUrl = `${getRedirectOrigin()}/auth/reset`;
  
  const { error } = await supabase.auth.resetPasswordForEmail(email, {
    redirectTo: redirectUrl
  });
  
  if (error) {
    throw error;
  }
}

/**
 * Update user password after recovery
 */
export async function updatePassword(newPassword: string): Promise<void> {
  const { error } = await supabase.auth.updateUser({ 
    password: newPassword 
  });
  
  if (error) {
    throw error;
  }
}